import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { supervisorService } from '../../application/SupervisorService';
import useErrorMiddleware from '../errorMiddleware/useErrorMiddleware';
import { getOrganizationResourcesMutationKey } from '../organization';
import { useShowSnackbarMessage } from '../snackbarMessage/useShowSnackbarMessage';
import { useRetrieveAllSupervisors } from './useRetrieveAllSupervisors';

type DeleteSupervisorsParams = { supervisorIds: string[] };

type UseDeleteSupervisorsProps = ServiceResponse<({ supervisorIds }: DeleteSupervisorsParams) => void, void>;

// Hooks act as 'Adapter' layer.
export const useDeleteSupervisors = (): UseDeleteSupervisorsProps => {
  const queryClient = useQueryClient();
  const { run: refetchSupervisors } = useRetrieveAllSupervisors();
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { showSnackbarMessage } = useShowSnackbarMessage();

  const { isLoading, isSuccess, isError, mutate } = useMutation<string[], unknown, DeleteSupervisorsParams>(
    ({ supervisorIds }: DeleteSupervisorsParams): Promise<string[]> =>
      supervisorService.deleteSupervisors(supervisorIds),
    {
      onSuccess: async (supervisorIds: string[]) => {
        queryClient.invalidateQueries(getOrganizationResourcesMutationKey());

        supervisorIds.map(async (supervisorId) => {
          await queryClient.invalidateQueries([`planner_supervisor`, supervisorId]);
        });

        refetchSupervisors();

        showSnackbarMessage({
          type: `success`,
          messageIntlId: `infoerror.delete_supervisors_success`,
        });
      },
      // If the mutation fails, use the context we returned above
      onError: (err: any, variables: DeleteSupervisorsParams, context: any) => {
        console.info(`Delete Supervisors Error: `, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }
        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  return {
    error: isError,
    isLoading: isLoading,
    success: isSuccess,
    run: ({ supervisorIds }: DeleteSupervisorsParams) => mutate({ supervisorIds }),
  };
};
